import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  forwardRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-multi-select-chip',
  templateUrl: './multi-select-chip.component.html',
  styleUrls: ['./multi-select-chip.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectChipComponent),
      multi: true
    }
  ]
})
export class MultiSelectChipComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() label = 'Select Items';
  @Input() options: any[] = [];
  @Input() labelKey: string = 'label';
  @Input() valueKey: string = 'value';
  @Output() selectedValuesChange = new EventEmitter<string>();

  selectedItems: any[] = [];
  isOpen = false;
  disabled = false;

  // Control Value Accessor methods
  private onChange = (value: any) => { };
  private onTouched = () => { };

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    this.initializeSelectedItems();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['options']) {
      this.initializeSelectedItems();
    }
  }

  private initializeSelectedItems() {
    if (this.selectedItems.length > 0 && this.options) {
      const selectedValueArray = this.selectedItems.map(item => item[this.valueKey].toString());
      this.selectedItems = this.options.filter(option =>
        selectedValueArray.includes(option[this.valueKey].toString())
      );
      this.cdr.markForCheck();
    }
  }

  // UI Event Handlers
  toggleDropdown() {
    if (!this.disabled) {
      this.isOpen = !this.isOpen;
      this.onTouched();
      this.cdr.markForCheck();
    }
  }

  toggleItem(item: any) {
    if (this.disabled) return;

    const index = this.selectedItems.findIndex(selectedItem =>
      selectedItem[this.valueKey] === item[this.valueKey]
    );
    if (index === -1) {
      this.selectedItems.push(item);
    } else {
      this.selectedItems.splice(index, 1);
    }
    this.updateSelectedValues();
  }

  removeItem(item: any, event: Event) {
    if (this.disabled) return;

    event.stopPropagation();
    const index = this.selectedItems.findIndex(selectedItem =>
      selectedItem[this.valueKey] === item[this.valueKey]
    );
    if (index !== -1) {
      this.selectedItems.splice(index, 1);
      this.updateSelectedValues();
    }
  }

  isSelected(item: any): boolean {
    return this.selectedItems.some(selectedItem =>
      selectedItem[this.valueKey] === item[this.valueKey]
    );
  }

  private updateSelectedValues() {
    const value = this.selectedItems.map(item => item[this.valueKey]).join(', ');
    this.onChange(value);
    this.selectedValuesChange.emit(value);
    this.cdr.markForCheck();
  }

  onMouseLeave() {
    this.isOpen = false;
    this.cdr.markForCheck();
  }

  // ControlValueAccessor Implementation
  writeValue(value: string): void {
    if (value && this.options) {
      const valueArray = value.split(',').map(v => v.trim());
      this.selectedItems = this.options.filter(option =>
        valueArray.includes(option[this.valueKey].toString())
      );
    } else {
      this.selectedItems = [];
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }
}
